import { ReactNode } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { IconContext } from 'react-icons';
import { TbNotesOff } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import Links from '../Links/Links';
import { getBackground } from 'utils';
import './ProfileView.css';

interface ProfileTheme {
    background?: {
        type?: 'solid' | 'gradient';
        solid?: string;
        gradient?: { left?: string; right?: string; direction?: string };
    };
    text?: { primary?: string; secondary?: string };
    button?: Record<string, any>;
}

interface ProfileInfo {
    title?: string;
    description?: string;
}

interface ProfileViewProps {
    links: any[];
    theme: ProfileTheme;
    info: ProfileInfo;
    /** Drives the empty-state: a profile with no username and no info is "empty". */
    username?: string | null;
    /** Avatar slot — pass <Avatar> for read-only contexts or <EditableAvatar> for the editor. */
    avatar?: ReactNode;
    /** Forwarded to <Links>: 'public' is interactive, 'preview' disables navigation. */
    origin?: 'public' | 'preview';
    /** Render the shared skeleton instead of content. */
    loading?: boolean;
    /** Whether ProfileView paints the themed background itself (default true). */
    paintBackground?: boolean;
    /** Extra modifier from the surrounding shell (scroll/max-height, etc.). */
    className?: string;
}

const ProfileView = ({
    links,
    theme,
    info,
    username,
    avatar,
    origin = 'public',
    loading = false,
    paintBackground = true,
    className = '',
}: ProfileViewProps) => {
    const { t } = useTranslation();

    const title = info?.title ?? '';
    const description = info?.description ?? '';
    const hasContent = Boolean(username) || title !== '' || description !== '';

    const style = paintBackground && !loading
        ? { background: getBackground(theme?.background) }
        : undefined;

    return (
        <div className={`profile-view ${className}`.trim()} style={style}>
          <div className="profile-view-inner">
            {loading ? (
                <>
                    <Skeleton variant="circular" width={130} height={130} className="profile-view-skeleton-avatar" />
                    <div className="profile-view-header">
                        <Skeleton variant="text" width="55%" height={40} className="skeleton-centered" />
                        <Skeleton variant="text" width="75%" height={24} className="skeleton-centered" />
                    </div>
                    <div className="profile-view-skeleton-links">
                        {[1, 2, 3].map(i => (
                            <Skeleton key={i} variant="rounded" height={48} className="profile-view-skeleton-pill" />
                        ))}
                    </div>
                </>
            ) : hasContent ? (
                <>
                    {avatar !== undefined && (
                        <div className="profile-view-avatar">{avatar}</div>
                    )}
                    <div className="profile-view-header">
                        <Typography
                            className="profile-view-title"
                            component="h4"
                            sx={{ color: theme?.text?.primary }}
                        >
                            {title.length > 0
                                ? title
                                : username
                                    ? <><span className="profile-view-at">@</span>{username}</>
                                    : null}
                        </Typography>
                        {description.length > 0 && (
                            <Typography
                                className="profile-view-body"
                                variant="body1"
                                sx={{ color: theme?.text?.secondary ?? theme?.text?.primary }}
                            >
                                {description}
                            </Typography>
                        )}
                    </div>
                    {links.length > 0 && (
                        <Links className="profile-view-links" links={links} theme={theme} origin={origin} />
                    )}
                </>
            ) : (
                <Box className="profile-view-empty">
                    <IconContext.Provider value={{ color: '#ffffff80', className: 'react-icons-color' }}>
                        <TbNotesOff fontSize={50} />
                    </IconContext.Provider>
                    <Typography variant="h6" sx={{ color: 'text.disabled' }}>
                        {t('profile.preview.empty-title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center' }}>
                        {t('profile.preview.empty-subtitle')}
                    </Typography>
                </Box>
            )}
          </div>
        </div>
    );
};

export default ProfileView;
